/**
 * Canonical Firestore collection names. Centralised so screens never hard-code
 * a string and the "not-yet-projected" set is explicit.
 */
export const COLLECTIONS = {
  // identity
  users: "users",
  userNameChanges: "userNameChanges",
  kycVerifications: "kycVerifications",
  accountDeletionRequests: "accountDeletionRequests",
  // Per-platform minimum supported app build. Redacted projection readable by every signed-in
  // user (no `reason` / `updatedBy`); the console fetches those via the admin GET.
  appVersionPolicies: "appVersionPolicies",
  // Runtime feature flags / maintenance kill-switches. Redacted projection readable by anyone,
  // before sign-in (no `reason` / `updatedBy`); the console fetches those via the admin GET.
  featureFlags: "featureFlags",
  // wallet
  wallets: "wallets",
  walletAvailability: "walletAvailability",
  activityEntries: "activityEntries",
  ledgerEntries: "ledgerEntries",
  walletTransactions: "walletTransactions",
  payments: "payments",
  cardRefunds: "cardRefunds",
  cardRefundProofSubmissions: "cardRefundProofSubmissions",
  completedCardRefunds: "completedCardRefunds",
  sessionKeys: "sessionKeys",
  // Admin-only (wallet-service 0.74.0): each session key's on-chain permission (flag, signer
  // module, whether it answers ERC-1271), and the per-wallet generation + legacy-key summary.
  // The owner-readable `sessionKeys` / `wallets` documents never carry the key internals.
  sessionKeyPermissions: "sessionKeyPermissions",
  walletKeyInventories: "walletKeyInventories",
  ownerOperations: "ownerOperations",
  walletMigrations: "walletMigrations",
  // Admin-only (wallet-service 0.75.0): the operator's v3.7 → v3.8 upgrade offer, one per wallet.
  // The owner sees only `wallets/{id}.upgradeOffer`.
  walletUpgradeOffers: "walletUpgradeOffers",
  // Owner- and admin-readable (wallet-service 0.78.0): each attempt to forward money that reached
  // a previous (migrated / retired) wallet onto the owner's newer wallet.
  residualForwards: "residualForwards",
  // Metadata ONLY — see `OwnerKeyBackupDoc`. The password-encrypted recovery secret itself is
  // never projected to Firestore and has no admin read surface anywhere.
  ownerKeyBackups: "ownerKeyBackups",
  // cross-chain
  depositAddresses: "depositAddresses",
  stablecoinReceiveAddresses: "stablecoinReceiveAddresses",
  crossChainDeposits: "crossChainDeposits",
  crossChainSends: "crossChainSends",
  // cards
  cards: "cards",
  cardNotifications: "cardNotifications",
  collateralWithdrawals: "collateralWithdrawals",
  // ramps
  deposits: "deposits",
  withdrawals: "withdrawals",
  virtualAccounts: "virtualAccounts",
  paymentAccounts: "paymentAccounts",
  // ramp sagas — the on-chain halves, owned by the wallet domain. These are where a stalled
  // ramp is actually diagnosable: `deposits` / `withdrawals` carry only the coarse state.
  forwarderSweeps: "forwarderSweeps",
  offrampBurns: "offrampBurns",
  // earn
  vaults: "vaults",
  earnMarketState: "earnMarketState",
  earnPositions: "earnPositions",
  earnOperations: "earnOperations",
  vaultApprovals: "vaultApprovals",
  // A wallet migration's earn drain, keyed by the migration id (earn-service 0.10.0).
  walletMigrationDrains: "walletMigrationDrains",
  // settlement-internal
  holds: "holds",
  settlementObligations: "settlementObligations",
  settlements: "settlements",
  reserveTopUpLegs: "reserveTopUpLegs",
  smartAccountOperations: "smartAccountOperations",
} as const;

export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];

/**
 * Collections the backend has NOT projected yet. The console shows an explanatory empty state
 * for these instead of the ordinary "no rows" one.
 *
 * Empty today. The five settlement-internal collections lived here until wallet `888ea8a`
 * shipped their projections; leaving them listed told an operator looking at a genuinely empty
 * collection that the backend had not built it yet. Keep this set honest — a stale entry is
 * worse than no entry, because it reads as a known gap rather than a real answer.
 */
export const NOT_YET_PROJECTED: ReadonlySet<CollectionName> = new Set([]);

export function isNotYetProjected(name: CollectionName): boolean {
  return NOT_YET_PROJECTED.has(name);
}
